import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { X, CheckCircle2, ArrowRight, Loader2 } from 'lucide-react';
import { trackEnrolmentConversion } from '../lib/tracking';
import { useEnrolmentModal } from '../context/EnrolmentModalContext';
import { WHATSAPP_NUMBER } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROGRAMMES = [
  'Pre-Nursery',
  'Nursery',
  'Primary',
  'Secondary',
  'Foundation / A-Level',
];

const emptyForm = {
  student_name: '',
  parent_name: '',
  email: '',
  phone: '',
  programme: '',
  date_of_birth: '',
  address: '',
  message: '',
};

const EnrolmentForm = () => {
  const { open, hide, defaults } = useEnrolmentModal();
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null); // enrolment record after success

  useEffect(() => {
    if (open) {
      setForm({ ...emptyForm, ...(defaults || {}) });
      setError('');
      setDone(null);
    }
  }, [open, defaults]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') hide(); };
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, hide]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e?.preventDefault?.();
    setError('');

    // Basic validation
    if (!form.student_name.trim() || !form.parent_name.trim() || !form.email.trim() || !form.phone.trim() || !form.programme) {
      setError('Please fill in all required fields.');
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/enrolments`, {
        ...form,
        source: 'website',
      });
      setDone(res.data);
      trackEnrolmentConversion(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Something went wrong. Please try again.';
      setError(typeof msg === 'string' ? msg : 'Please review the form and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const whatsappFollowUp = (record) => {
    const text =
      `Hello Whiteplains British Schools, I have just submitted an enrolment enquiry.\n\n` +
      `• Student: ${record.student_name}\n` +
      `• Parent: ${record.parent_name}\n` +
      `• Programme: ${record.programme}\n` +
      `• Phone: ${record.phone}\n` +
      `Reference: ${record.id}`;
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-center justify-center p-3 md:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          {/* backdrop */}
          <motion.div
            className="absolute inset-0 bg-[#0f0d0b]/80 backdrop-blur-sm"
            onClick={hide}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* modal card */}
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative bg-white w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl border border-[#c8942a]/40"
          >
            {/* Close */}
            <button onClick={hide} aria-label="Close" className="absolute top-4 right-4 z-10 w-10 h-10 grid place-items-center bg-white hover:bg-[#f3ead9] border border-[#eadfc9] transition-colors">
              <X size={18} />
            </button>

            {/* Header */}
            <div className="bg-[#0f0d0b] text-white p-6 md:p-8 relative overflow-hidden">
              <div className="absolute inset-0 opacity-10" style={{
                backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
                backgroundSize: '40px 40px',
              }} />
              <div className="relative">
                <div className="font-sans-pro text-[11px] uppercase tracking-[0.4em] text-[#f0c95a] font-semibold">Academic Session 2026 / 2027</div>
                <h2 className="mt-3 font-serif-display text-3xl md:text-4xl font-black leading-tight">
                  Begin your family&apos;s <span className="italic text-[#f0c95a]">Whiteplains journey</span>
                </h2>
                <p className="mt-2 text-white/70 text-sm max-w-xl">
                  Complete the form below and our admissions team will reach out within 24 hours.
                </p>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 md:p-8">
              {done ? (
                <div className="text-center py-6">
                  <motion.div
                    initial={{ scale: 0, rotate: -20 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: 'spring', stiffness: 240, delay: 0.1 }}
                    className="mx-auto w-16 h-16 rounded-full bg-[#f3ead9] grid place-items-center text-[#8f6417]"
                  >
                    <CheckCircle2 size={34} />
                  </motion.div>
                  <h3 className="mt-6 font-serif-display text-2xl md:text-3xl font-black text-[#1c1a17]">Application received</h3>
                  <p className="mt-2 text-[#4a4238] text-[15px] max-w-md mx-auto">
                    Thank you, <span className="font-medium">{done.parent_name}</span>. Our admissions team will contact you
                    shortly regarding <span className="font-medium">{done.student_name}</span>.
                  </p>
                  <div className="mt-1 text-[11px] uppercase tracking-[0.3em] text-[#8f6417] font-sans-pro">
                    Reference · {done.id.slice(0, 8).toUpperCase()}
                  </div>

                  <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                    <a
                      href={whatsappFollowUp(done)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary !bg-[#25D366] !shadow-none inline-flex items-center gap-2"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.15-.174.199-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.29.173-1.414-.074-.124-.272-.198-.57-.347zM12.037 21.5h-.008a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.981.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.002-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.822 9.822 0 0 1 2.894 6.994c-.003 5.45-4.437 9.885-9.882 9.885zm8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.82 11.82 0 0 0-3.492-8.7z" />
                      </svg>
                      Continue on WhatsApp
                    </a>
                    <button onClick={hide} className="btn-outline">Close</button>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Student's full name" required>
                      <input value={form.student_name} onChange={set('student_name')} type="text" required className="wbs-input" placeholder="e.g. Ada Okoye" />
                    </Field>
                    <Field label="Date of birth">
                      <input value={form.date_of_birth} onChange={set('date_of_birth')} type="date" className="wbs-input" />
                    </Field>
                    <Field label="Parent / Guardian name" required>
                      <input value={form.parent_name} onChange={set('parent_name')} type="text" required className="wbs-input" placeholder="Full name" />
                    </Field>
                    <Field label="Programme of interest" required>
                      <select value={form.programme} onChange={set('programme')} required className="wbs-input">
                        <option value="">Select a programme…</option>
                        {PROGRAMMES.map((p) => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Email address" required>
                      <input value={form.email} onChange={set('email')} type="email" required className="wbs-input" placeholder="parent@example.com" />
                    </Field>
                    <Field label="Phone / WhatsApp" required>
                      <input value={form.phone} onChange={set('phone')} type="tel" required className="wbs-input" placeholder="0803 345 3457" />
                    </Field>
                  </div>
                  <Field label="Home address">
                    <input value={form.address} onChange={set('address')} type="text" className="wbs-input" placeholder="City, State" />
                  </Field>
                  <Field label="A short message (optional)">
                    <textarea value={form.message} onChange={set('message')} rows={3} className="wbs-input" placeholder="Anything you'd like our admissions team to know…" />
                  </Field>

                  {error && (
                    <div className="text-sm text-[#b8331f] bg-[#fdecea] border border-[#f3c1bb] px-4 py-2.5">
                      {error}
                    </div>
                  )}

                  <div className="pt-2 flex flex-wrap items-center justify-between gap-4">
                    <p className="text-[11px] uppercase tracking-[0.28em] text-[#8f6417] font-sans-pro">
                      By submitting you agree to be contacted about admissions.
                    </p>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="btn-primary disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {submitting ? (<><Loader2 size={16} className="animate-spin" /> Submitting</>) : (<>Submit Application <ArrowRight size={16} /></>)}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <div className="text-[11px] uppercase tracking-[0.3em] text-[#8f6417] font-sans-pro mb-1.5">
      {label}{required && <span className="text-[#b8331f]"> *</span>}
    </div>
    {children}
  </label>
);

export default EnrolmentForm;
